/**
 * ⭐ **MEDYA EFEKTLERİ POLİTİKASI — SAF KATMAN** (Rapor 06 §6, F4-07/F4-08).
 *
 * ### ⚠️ Kabul kriteri: *"zayıf cihazda otomatik kapanıyor"*
 * Bu, efektlerin **var olma koşuludur**. Arka plan bulanıklığı her karede
 * segmentasyon çalıştırır; orta seviye bir telefonda bu, görüntüyü dondurur
 * ve kullanıcı toplantıyı terk eder. Efekt açık kalıp donmaktansa **kapanıp
 * çalışmak** her zaman doğrudur.
 *
 * ### ⚠️ Kullanıcı tercihi ile otomatik kapanma AYRI ŞEYLERDİR
 * Kullanıcı bulanıklığı açtıysa ve cihaz yetişemiyorsa efekt kapanır ama
 * **tercih korunur**: daha güçlü bir cihazda veya yük düştüğünde geri açılır.
 * Tercihi silmek, kullanıcının ayarını sessizce ezmektir.
 */

export type BlurLevel = "off" | "light" | "strong" | "image";
export type NoiseLevel = "off" | "browser" | "rnnoise";

export interface EffectPreferences {
  blur: BlurLevel;
  /** `blur: "image"` iken kullanılacak arka plan (data URL veya /assets yolu). */
  backgroundImage: string | null;
  noise: NoiseLevel;
}

/**
 * ⚠️ Varsayılan **`browser`**dır, `off` değil: tarayıcının kendi gürültü
 *    engellemesi (`noiseSuppression: true`) zaten açıktır ve kapatmak yankı
 *    ile geri besleme üretir. `rnnoise` bunun **üzerine** ek kazançtır.
 *
 * ⚠️ Bulanıklık varsayılanı **kapalıdır**: CPU maliyeti olan bir özelliği
 *    kullanıcı istemeden açmak, zayıf cihazlarda ilk izlenimi bozar.
 */
export const DEFAULT_EFFECTS: EffectPreferences = Object.freeze({
  blur: "off",
  backgroundImage: null,
  noise: "browser",
});

/**
 * Cihazın efekt taşıyıp taşıyamayacağı.
 *
 * ⚠️ `hardwareConcurrency` **tek başına yetmez** ama elde olan en iyi
 *    sinyaldir: 2 çekirdekli bir cihazda segmentasyon + kodlama + render
 *    aynı iş parçacığı havuzunu paylaşır.
 *
 * ⚠️ `deviceMemory` yalnız Chromium'da vardır; yokluğu "yetersiz" sayılmaz —
 *    Safari'de her cihazı zayıf ilan etmek özelliği tamamen kapatırdı.
 */
export function deviceCapability(input: {
  hardwareConcurrency: number;
  deviceMemoryGb: number | null;
  isMobile: boolean;
}): "strong" | "moderate" | "weak" {
  const cores = input.hardwareConcurrency;
  const mem = input.deviceMemoryGb;

  if (cores <= 2) return "weak";
  if (mem !== null && mem <= 2) return "weak";
  // ⚠️ Mobilde eşik daha yüksektir: aynı çekirdek sayısı termal kısıtlama
  //    altında masaüstünün çok altında iş çıkarır.
  if (input.isMobile) return cores >= 8 && (mem === null || mem >= 4) ? "moderate" : "weak";
  return cores >= 8 ? "strong" : "moderate";
}

/**
 * ⚠️ Bulanıklık **zayıf cihazda hiç başlatılmaz**. Başlatıp sonra kapatmak,
 *    kullanıcıya birkaç saniye donmuş görüntü göstermek demektir.
 */
export function blurAllowed(capability: ReturnType<typeof deviceCapability>): boolean {
  return capability !== "weak";
}

/**
 * ⚠️ RNNoise **her cihazda** çalışabilir: AudioWorklet ayrı bir iş
 *    parçacığındadır ve 480 örneklik çerçevelerde maliyeti düşüktür.
 *    Bulanıklıkla aynı kapıya bağlamak, gereksiz bir kısıtlama olurdu.
 */
export function noiseAllowed(_capability: ReturnType<typeof deviceCapability>): boolean {
  return true;
}

/** Kare süresi bu eşiği aşarsa efekt **düşürülür** (60 fps ≈ 16,7 ms). */
export const FRAME_BUDGET_MS = 33;
/** Ardışık kaç bütçe aşımından sonra düşürülür. */
export const DEGRADE_AFTER_BREACHES = 30;

export type EffectAction = "keep" | "degrade" | "disable";

/**
 * Kare süresi ölçümünden efekt kararı.
 *
 * ### ⚠️ Tek bir yavaş kare KARAR VERDİRMEZ
 * Sekme değişimi, çöp toplama veya başka bir uygulamanın yükü tek karelik
 * sıçramalar üretir. Eşiğe **ardışık** ihlal sayısı bakar; yoksa efekt sürekli
 * açılıp kapanır ve bu, donmadan daha rahatsız edicidir.
 *
 * ### ⚠️ `strong` → `light` → `off` kademelidir
 * Doğrudan kapatmak, kullanıcının arka planını bir anda görünür kılar —
 * mahremiyet açısından ani ve istenmeyen bir değişimdir.
 */
export function decideEffect(input: {
  current: BlurLevel;
  consecutiveBreaches: number;
}): EffectAction {
  if (input.current === "off") return "keep";
  if (input.consecutiveBreaches < DEGRADE_AFTER_BREACHES) return "keep";
  return input.current === "light" ? "disable" : "degrade";
}

/** Bir kademe aşağı — `image` ve `strong` önce `light`a düşer. */
export function degradeBlur(level: BlurLevel): BlurLevel {
  if (level === "off") return "off";
  if (level === "light") return "off";
  return "light";
}

/**
 * Bulanıklık yarıçapı (piksel).
 *
 * ⚠️ `strong` bile **tam opak değildir**: arka planı tamamen gizlemek için
 *    segmentasyonun kusursuz olması gerekir ve değildir. Kenar hatalarında
 *    yarı saydam bir bulanıklık, sert bir maskeden çok daha az göze batar.
 */
export function blurRadiusPx(level: BlurLevel): number {
  switch (level) {
    case "light":
      return 8;
    case "strong":
      return 18;
    case "image":
    case "off":
      return 0;
  }
}
